/**
 * SessionContext — React Context for SMART launch session state.
 *
 * Security notes:
 * - PHI is stored ONLY in React component state (in-memory).
 * - No localStorage, sessionStorage, cookies, or IndexedDB.
 * - Context is destroyed when the React tree unmounts (chart close).
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { LaunchContext, EHRTheme, AppMode, WorkflowMode, ProviderSpecialty } from '../types';
import { PROVIDER_SPECIALTIES } from '../utils/specialtyMapping';
import { auth } from '../services/firebase';

interface SessionState {
  launch: LaunchContext;
  isLoading: boolean;
  error: string | null;
  /** True if the clinician has accepted the ToS for this session */
  tosAccepted: boolean;
  /** ID of the currently-expanded evidence panel */
  expandedSuggestionId: string | null;
  workflowMode: WorkflowMode;
}

interface SessionActions {
  setLaunch: (ctx: LaunchContext) => void;
  setLoading: (v: boolean) => void;
  setError: (msg: string | null) => void;
  acceptTos: () => void;
  setExpandedSuggestion: (id: string | null) => void;
  setWorkflowMode: (mode: WorkflowMode) => void;
}

const defaultLaunch: LaunchContext = {
  mode: 'mock',
  theme: 'cerner',
  fhirPatientId: null,
  raapidPatientId: 'CRN-PAT-00482',
  providerName: null,
  providerNpi: null,
  providerSpecialty: null,
  encounterId: null,
  smartClient: null,
};

const SessionContext = createContext<(SessionState & SessionActions) | null>(null);

/** Parse URL search params to determine mode and theme */
function parseLaunchParams(): Partial<LaunchContext> {
  const params = new URLSearchParams(window.location.search);
  const mode = (params.get('mode') as AppMode) ?? 'mock';
  const theme = (params.get('theme') as EHRTheme) ?? 'cerner';
  const raapidPatientId = params.get('patientId') ?? params.get('mrn') ?? 'CRN-PAT-00482';
  const specialtyParam = params.get('specialty');
  const providerSpecialty = specialtyParam && PROVIDER_SPECIALTIES.includes(specialtyParam as ProviderSpecialty)
    ? (specialtyParam as ProviderSpecialty)
    : null;
  const providerName = params.get('providerName');
  return { mode, theme, raapidPatientId, providerSpecialty, ...(providerName ? { providerName } : {}) };
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [launch, setLaunchState] = useState<LaunchContext>(() => ({
    ...defaultLaunch,
    ...parseLaunchParams(),
  }));
  const [isLoading, setLoadingState] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return (params.get('mode') as AppMode) === 'smart';
  });
  const [error, setErrorState] = useState<string | null>(null);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [expandedSuggestionId, setExpandedSuggestionState] = useState<string | null>(null);
  const [workflowMode, setWorkflowModeState] = useState<WorkflowMode>('point-of-care');

  // Sync logged-in Firebase user's display name into providerName
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      if (user) {
        const name = user.displayName ?? user.email ?? 'Provider';
        setLaunchState((prev) => ({ ...prev, providerName: prev.providerName ?? name }));
      }
    });
    return unsub;
  }, []);

  const setLaunch = useCallback((ctx: LaunchContext) => setLaunchState(ctx), []);
  const setLoading = useCallback((v: boolean) => setLoadingState(v), []);
  const setError = useCallback((msg: string | null) => setErrorState(msg), []);
  const acceptTos = useCallback(() => setTosAccepted(true), []);
  const setExpandedSuggestion = useCallback(
    (id: string | null) =>
      setExpandedSuggestionState((prev) => (prev === id ? null : id)),
    [],
  );
  const setWorkflowMode = useCallback((mode: WorkflowMode) => setWorkflowModeState(mode), []);

  return (
    <SessionContext.Provider
      value={{
        launch,
        isLoading,
        error,
        tosAccepted,
        expandedSuggestionId,
        workflowMode,
        setLaunch,
        setLoading,
        setError,
        acceptTos,
        setExpandedSuggestion,
        setWorkflowMode,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
